import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { getColorByString } from "@msuite/utils/colors";
import { guardString } from "@msuite/utils/strings";
import React, { memo, useMemo } from "react";
import { AddTicketModal } from "@/modals/projects/add-ticket-modal";
import {
	CapacityCalendarCells,
	CapacityCalendarRowWrapper,
	useCapacityCalendarContext,
} from "../capacity-calendar";
import { CapacityPlannerBar, CapacityPlannerRowHead } from "../planner-view";
import { CapacityManagerTicketRow } from "./capacity-manager-ticket-row";
import { useTicketCoverage } from "./use-ticket-coverage";
import type { CapacityProjectListItem } from "./types";

/** Props Interface */
interface CapacityManagerProjectRowProps {
	item: CapacityProjectListItem;
}

function CapacityManagerProjectRowImpl({ item }: CapacityManagerProjectRowProps) {
	/** Memoized */
	// The roll-up takes a list, and a project is a list of one — held steady so the memo behind it
	// is not rebuilt on every render.
	const coverageProjects = useMemo(() => [item], [item]);

	/** Context */
	const { getIsExpanded, toggleExpanded } = useCapacityCalendarContext();

	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const addTicketModal = useDisclosure();

	/** Functions */
	// The modal invalidates by project id, and this board reads tickets by window —
	// `["tickets", "scheduled", …]` carries no project — so nothing it invalidates matches here.
	// Refreshing as the modal closes covers the new ticket without changing what `onComplete` means
	// to the three screens that share the modal. A cancel costs one refetch of one query.
	async function handleOnCloseAddTicket() {
		addTicketModal.onClose();
		await qc.invalidateQueries({ predicate: invalidate(["tickets"]) });
	}

	/** Computed */
	// The project is only on the board because the window reaches one of its tickets, so the
	// handle always has something under it.
	const isExpanded = getIsExpanded(item.projectId);

	// What the row says with its tickets closed: the days the site carries work, and the days some
	// of that work has nobody on it. Drawn open as well — the ticket rows below say which ticket
	// each day belongs to, and this says how the project reads as a whole.
	const accentColor = getColorByString(item.name);
	const coverageGradient = useTicketCoverage(coverageProjects, accentColor);

	/** Render */
	return (
		<React.Fragment>
			<CapacityCalendarRowWrapper variant="sub">
				<CapacityPlannerRowHead
					accentColor={accentColor}
					handle={
						<Button
							variant="white"
							size="xxs"
							icon={<Icon name={isExpanded ? "ChevronDown" : "ChevronRight"} />}
							onClick={() => toggleExpanded(item.projectId)}
						/>
					}
					actions={
						<Button
							variant="white"
							size="xxs"
							icon={<Icon name="Plus" />}
							onClick={addTicketModal.onOpen}
						/>
					}
				>
					<p className="text-sm truncate">{item.name}</p>
					<p className="label truncate">
						{`${guardString(item.number)} · ${item.city} · ${t("planning.crews.tickets_count", {
							count: String(item.tickets.length),
						})}`}
					</p>
				</CapacityPlannerRowHead>
				<CapacityCalendarCells />
				<CapacityPlannerBar
					gradient={coverageGradient}
					thickness="slim"
				/>
			</CapacityCalendarRowWrapper>
			{isExpanded &&
				item.tickets.map((ticket) => (
					<CapacityManagerTicketRow
						key={ticket.id}
						ticket={ticket}
						weekdays={item.weekdays}
					/>
				))}
			{addTicketModal.isOpen && (
				<AddTicketModal
					isOpen={addTicketModal.isOpen}
					onClose={handleOnCloseAddTicket}
					projectId={item.projectId}
				/>
			)}
		</React.Fragment>
	);
}

export const CapacityManagerProjectRow = memo(CapacityManagerProjectRowImpl);
